import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DrawerHeaderButton } from 'design-system';
import { ArrowLeft, X } from 'lucide-react';
import { useParams } from 'react-router';

import { useUpdateDashboard } from '@/features/dashboard/api';
import { DashboardDrawerLayout } from '@/features/dashboard/components/Drawer/components/DashboardDrawerLayout';
import {
  DashboardGroupedSelect,
  type DashboardSelectOption
} from '@/features/dashboard/components/Drawer/components/DashboardGroupedSelect';
import { DrawerFooterButtons } from '@/features/dashboard/components/Drawer/components/DrawerFooterButtons';
import { useDrawerSteps } from '@/features/dashboard/components/Drawer/context/DrawerStepsContext';
import { DrawerHeader } from '@/features/dashboard/components/Drawer/DrawerHeader';
import Heading from '@/features/dashboard/components/Heading';
import { Preview } from '@/features/dashboard/components/Preview';
import { HIGHLIGHTED_PRICE_MAX_ITEMS } from '@/features/dashboard/constants/const';
import { useBuildDashboardStructure } from '@/features/dashboard/hooks/sync/useBuildDashboardStructure';
import { getWidgetAnimationIds } from '@/features/dashboard/hooks/sync/useBuildDashboardStructure.utils';
import { useCurrentDashboardTabConfig } from '@/features/dashboard/hooks/useCurrentDashboardConfig';
import { useInitialDashboardName } from '@/features/dashboard/hooks/useInitialDashboardName';
import { useWidgetOptions } from '@/features/dashboard/hooks/useWidgetOptions';
import getWidgetType from '@/features/dashboard/lib/getWidgetType';
import { useDashboardEditActions } from '@/features/dashboard/pages/Edit/hooks/useDashboardEditActions';
import {
  selectCurrentTabId,
  selectCurrentTiles
} from '@/features/dashboard/state/dashboardEdit.selectors';
import { useWidgetAnimation } from '@/features/dashboard/state/WidgetAnimation/hooks';
import { useMarketWidgets } from '@/features/markets';
import { useMarketsList } from '@/features/markets/api/marketsList.options';
import useSnackbarNotifications from '@/shared/hooks/useSnackbarNotifications';
import { useAppSelector } from '@/store/hooks';

import {
  buildImportedAppFromWidget,
  buildSuccessMessage,
  computeNextApps,
  handleEditModeImport
} from './importWidget';

export const ImportMarketWidget = () => {
  const [select, setSelect] = useState<{
    market: string | null;
    tab: string | null;
    widget: string | null;
  }>({
    market: null,
    tab: null,
    widget: null
  });

  const { data: marketsListData } = useMarketsList();
  const groupedMarkets = marketsListData?.groupedMarkets ?? [];

  const marketsOptions = useMemo(
    () =>
      groupedMarkets
        .map(group => {
          return {
            label: group.name,
            options: group.markets
              .filter(m => !m.comingSoon)
              .map(market => ({
                label: market.name,
                value: market.marketId as string
              }))
          };
        })
        .filter(group => group.options.length > 0),
    [groupedMarkets]
  );

  const { data, isLoading } = useMarketWidgets(select.market);

  const tabOptions = useMemo(() => {
    if (!data) return [];
    return data.map(tab => ({
      label: tab.name,
      value: tab.name
    }));
  }, [data]);

  const selectedTab = useMemo(() => {
    if (!data) return;
    return data.find(tab => tab.name === select.tab);
  }, [data, select.tab]);

  const widgetOptions = useWidgetOptions(selectedTab);

  const handleMarketChange = ({ value }: DashboardSelectOption) => {
    if (isPending) return;
    setSelect(() => ({ market: value, tab: null, widget: null }));
    setHasChanges(true);
  };

  const handleTabChange = ({ value }: DashboardSelectOption) => {
    if (isPending) return;
    setSelect(prev => ({ ...prev, tab: value, widget: null }));
    setHasChanges(true);
  };

  const handleWidgetChange = ({ value }: DashboardSelectOption) => {
    if (isPending) return;
    setSelect(prev => ({ ...prev, widget: value }));
    setHasChanges(true);
  };

  const selectedWidget = useMemo(() => {
    if (!selectedTab || !select.widget) return null;

    return (
      selectedTab.widgets.find(w => `${w.name}-{${getWidgetType(w)}}` === select.widget) || null
    );
  }, [selectedTab, select.widget]);
  const dashboardName = useInitialDashboardName();
  const currentTabId = useAppSelector(selectCurrentTabId);
  const currentTiles = useAppSelector(selectCurrentTiles);
  const { tabName: rawTabName } = useParams<{ tabName?: string }>();

  const currentTabName = rawTabName ? decodeURIComponent(rawTabName) : undefined;
  const successMessageRef = useRef('');

  const { mutate, isPending } = useUpdateDashboard(
    dashboardName,
    () => successMessageRef.current,
    currentTabName
  );

  const {
    closeDrawer,
    setHasChanges,
    setPreventClose,
    handleAttemptBack,
    isEditMode,
    editingTarget
  } = useDrawerSteps();
  const actions = useDashboardEditActions();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbarNotifications();

  useEffect(() => {
    // Block drawer closing on outside click while update is pending
    setPreventClose(isPending);
  }, [isPending, setPreventClose]);

  const { setElementsToAnimateId } = useWidgetAnimation();
  const dashboardConfig = useCurrentDashboardTabConfig();

  const templateWidget = useMemo(() => {
    if (!selectedWidget) return;

    return buildImportedAppFromWidget(selectedWidget, dashboardConfig || []);
  }, [dashboardConfig, selectedWidget]);

  const buildDashboardStructure = useBuildDashboardStructure();

  const templateWidgetWithoutId = useMemo(() => {
    if (!templateWidget) return;
    if ('type' in templateWidget) {
      return { ...templateWidget, tiles: templateWidget.tiles.map(tile => ({ ...tile, id: '' })) };
    }
    return { ...templateWidget, id: '' };
  }, [templateWidget]);

  const handleSave = useCallback(() => {
    if (!templateWidget) return;

    setElementsToAnimateId(getWidgetAnimationIds(templateWidget));

    if (isEditMode) {
      if (!currentTabId) return;
      handleEditModeImport({
        actions,
        currentTabId,
        currentTiles,
        templateWidget,
        editingTarget,
        showErrorSnackbar,
        showSuccessSnackbar
      });
      closeDrawer();
      return;
    }

    if (isPending) return;

    const result = computeNextApps(dashboardConfig, templateWidget);

    if (result.exceeded) {
      const tilesLabel = `tile${result.limitedCount === 1 ? '' : 's'}`;
      showErrorSnackbar(
        `Tile limit of ${HIGHLIGHTED_PRICE_MAX_ITEMS} exceeded. Only ${result.limitedCount} ${tilesLabel} added: ${result.addedCodes.join(', ')}`,
        5000,
        'tiles-limit-exceeded-error'
      );
    }

    successMessageRef.current = buildSuccessMessage(result);
    mutate(buildDashboardStructure(templateWidget));
    // Optimistic close: the cache/route update in onMutate is already optimistic,
    // so the drawer closes immediately rather than waiting on the network round trip.
    // A failed save shows an error snackbar and rolls back via onError.
    closeDrawer();
  }, [
    actions,
    buildDashboardStructure,
    closeDrawer,
    currentTabId,
    currentTiles,
    dashboardConfig,
    editingTarget,
    isEditMode,
    isPending,
    mutate,
    setElementsToAnimateId,
    showErrorSnackbar,
    showSuccessSnackbar,
    templateWidget
  ]);

  return (
    <DashboardDrawerLayout
      footer={
        <DrawerFooterButtons
          disabled={!selectedWidget || isPending}
          onDiscard={closeDrawer}
          onSave={handleSave}
        />
      }
      header={
        <DrawerHeader
          before={
            <DrawerHeaderButton
              disabled={isPending}
              label='Go back'
              onClick={handleAttemptBack}
            >
              <ArrowLeft />
            </DrawerHeaderButton>
          }
          buttons={
            <DrawerHeaderButton
              disabled={isPending}
              label='Discard'
              onClick={() => {
                if (!isPending) closeDrawer();
              }}
            >
              <X />
            </DrawerHeaderButton>
          }
          title='Add new app'
        />
      }
      subheader={
        <Heading
          subtitle='Select your market widget'
          title='Configure import market widget'
        />
      }
    >
      <div className='flex flex-col gap-6'>
        <DashboardGroupedSelect
          disabled={isPending}
          label='Select market'
          options={marketsOptions}
          placeholder='Select market'
          value={select.market}
          onChange={handleMarketChange}
        />
        <DashboardGroupedSelect
          disabled={isPending || !select.market || tabOptions.length === 0}
          isLoading={isLoading}
          label='Select page'
          options={tabOptions}
          placeholder='Select page'
          value={select.tab}
          onChange={handleTabChange}
        />
        <DashboardGroupedSelect
          showPreview
          disabled={isPending || !select.tab}
          isLoading={isLoading}
          label='Select widget'
          options={widgetOptions}
          placeholder='Select widget'
          value={select.widget}
          onChange={handleWidgetChange}
        />
        <Preview
          enabled={!!templateWidgetWithoutId}
          template={templateWidgetWithoutId}
        />
      </div>
    </DashboardDrawerLayout>
  );
};
